import { Injectable, Injector, OnDestroy } from "@angular/core";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { EntityPersistentService } from "@common/lib/data/entity-persistent.service";
import { Logger } from "@common/lib/logger/logger";
import { BaseService } from "@common/service/base.service";
import { defer, distinctUntilKeyChanged, from, lastValueFrom, Observable, of, ReplaySubject, shareReplay, takeUntil } from "rxjs";
import { first, map, startWith, switchMap, takeWhile, tap } from "rxjs/operators";
import { ActorRefFrom, createActor, fromPromise, SnapshotFrom } from "xstate";
import { OrganisationActions, OrganisationEvent, OrganisationEventActions, OrganisationEventParameters, OrganisationMachine, OrganisationState } from "./organisation-machine";
import { IOrganisationService } from "./organisation-service.interface";

// This is the state machine context of the organisation state transitions as defined here:
// https://adaptbydesign.atlassian.net/wiki/spaces/DEV/pages/556564483/2018-10+Organisation+Service+Initialisation+Sequence+and+State+Transitions
@Injectable({
    providedIn: "root",
})
export class OrganisationStateService extends BaseService implements OnDestroy {
    public readonly log = Logger.getLogger(this.constructor.name);

    private orgService?: IOrganisationService;

    public readonly stateActions: OrganisationEventActions = {
        setOrganisationEntity: this.generateAction(OrganisationEvent.SetOrganisationEntity),
        updateOrganisationEntity: this.generateAction(OrganisationEvent.UpdateOrganisationEntity),
        waitForPerson: this.generateAction(OrganisationEvent.WaitForPerson),
        logout: this.generateAction(OrganisationEvent.Logout),
        initialise: this.generateAction(OrganisationEvent.Initialise),
        doPostInitialisation: this.generateAction(OrganisationEvent.DoPostInitialisation),
        switchOrganisation: this.generateAction(OrganisationEvent.SwitchOrganisation),
        fallbackSwitch: this.generateAction(OrganisationEvent.FallbackSwitch),
    };

    private readonly machineActor: ActorRefFrom<typeof OrganisationMachine>;
    private readonly state$: Observable<SnapshotFrom<typeof this.machineActor>>;
    private readonly unsubscribeSubject$ = new ReplaySubject<void>(1);
    private readonly unsubscribe$ = this.unsubscribeSubject$.asObservable();

    public constructor(
        injector: Injector,
        private entityPersistentService: EntityPersistentService,
    ) {
        super(injector);

        // add the concrete implementations for actions and actors
        const populatedMachine = OrganisationMachine.provide({
            actions: {
                [OrganisationActions.InitialiseWithOrganisationEntity]: () => this.entityPersistentService.initialise(),
                // setTimeout needed as the snapshot hasn't updated until all the actions finish
                [OrganisationActions.NotifyOrganisationEntityUpdatedEvent]: () => setTimeout(() => this.organisationService!.notifyOrganisationEntityUpdatedEvent()),
                [OrganisationActions.NotifyOrganisationChangingEvent]: () => this.organisationService!.notifyOrganisationChangingEvent(),
                [OrganisationActions.NotifyOrganisationChangedEvent]: () => this.organisationService!.notifyOrganisationChangedEvent(),
            },
            actors: {
                [OrganisationActions.PromiseToPerformOrganisationSwitch]: fromPromise(({ input }) =>
                    this.orgService!.promiseToPerformOrganisationSwitch(input)),
            },
        });

        this.machineActor = createActor(populatedMachine);

        this.state$ = from(this.machineActor).pipe(
            shareReplay(1),
            takeUntil(this.unsubscribe$),
        );

        // logging subscription
        this.state$.pipe(
            distinctUntilKeyChanged("value"),
            takeUntil(this.unsubscribe$),
        ).subscribe((snapshot) => this.log.info("Organisation state changed to:", snapshot.value));

        this.machineActor.start();
    }

    // used internally for unit test
    public get stateTransitioned$() {
        return this.state$;
    }

    public get currentState() {
        return this.machineActor.getSnapshot();
    }

    public get organisation() {
        return this.currentState.context.organisation;
    }

    public get organisationService() {
        return this.orgService;
    }

    public set organisationService(value: IOrganisationService | undefined) {
        this.orgService = value;
    }

    public isStateWithOrganisationEntity() {
        const currentState = this.currentState;
        return !currentState.matches(OrganisationState.InitialState)
            && !currentState.matches(OrganisationState.AwaitingUserInitialisationState);
    }

    public isOrganisationReady() {
        return this.currentState.matches(OrganisationState.OrganisationReadyState);
    }

    public ngOnDestroy() {
        this.machineActor.stop();
        this.unsubscribeSubject$.next();
    }

    @Autobind
    public waitForOrganisationEntity() {
        return defer(() => this.isStateWithOrganisationEntity()
            ? of(void 0)
            : this.state$.pipe(
                startWith(undefined),
                takeWhile((s) => !s
                    || s.matches(OrganisationState.InitialState)
                    || s.matches(OrganisationState.AwaitingUserInitialisationState)),
                map(() => void 0),
            ));
    }

    @Autobind
    public promiseToWaitForOrganisationReady() {
        return lastValueFrom(defer(() => this.isOrganisationReady()
            ? of(void 0)
            : this.state$.pipe(
                // need this 'startWith' so that we still get an emit even if the first emit from state$ not matching the takeWhile condition
                // - or the promise will throw an error (no element from sequence) - and org validation will fail!
                startWith(undefined),
                takeWhile((s) => !s || !s.matches(OrganisationState.OrganisationReadyState)),
                map(() => void 0),
            ))) as Promise<void>;
    }

    @Autobind
    public promiseToWaitForOrganisationSwitchCompletion() {
        return lastValueFrom(this.state$.pipe(
            first((s) => s.matches(OrganisationState.OrganisationReadyState)),
            tap(() => this.log.debug("Switched organisation")),
            switchMap((s) => s.context.error
                ? Promise.reject(s.context.error)
                : Promise.resolve()),
        ));
    }

    private generateAction<T extends OrganisationEvent>(type: T) {
        return (data?: OrganisationEventParameters<T>) => {
            const event = Object.assign({ type }, data);
            this.log.debug("Sending event to machine:", event);
            this.machineActor.send(event);
        };
    }
}
